import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { TrendingUp, Search, MapPin, BarChart3, Eye } from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

export default function MarketInsights() {
  const { t } = useTranslation();
  const [demandData, setDemandData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const fetchDemandData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/market/demand-insights`, {
        headers: getAuthHeader()
      });
      setDemandData(response.data);
    } catch (error) {
      toast.error(t('error.generic'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, t]);

  useEffect(() => {
    if (!user || user.role !== 'seller') {
      navigate('/');
      return;
    }
    fetchDemandData();
  }, [user, navigate, fetchDemandData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center mb-3">
            <BarChart3 className="w-8 h-8 text-[#F59E0B] mr-3" />
            <h1 className="text-4xl font-bold text-white" data-testid="market-insights-title">
              {t('insights.title')}
            </h1>
          </div>
          <p className="text-lg text-[#94A3B8]">
            {t('insights.subtitle')}
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center justify-between mb-2">
              <Search className="w-8 h-8 text-[#64748B]" />
              <span className="text-3xl font-bold text-white">{demandData.total_searches}</span>
            </div>
            <p className="text-[#94A3B8] font-medium">Total Searches (7 days)</p>
            <p className="text-sm text-[#64748B] mt-1">Buyers actively looking for minerals</p>
          </div>

          <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-[#F59E0B]" />
              <span className="text-3xl font-bold text-[#F59E0B]">{demandData.top_categories.length}</span>
            </div>
            <p className="text-[#94A3B8] font-medium">Active Categories</p>
            <p className="text-sm text-[#64748B] mt-1">Categories with buyer interest</p>
          </div>

          <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center justify-between mb-2">
              <MapPin className="w-8 h-8 text-[#64748B]" />
              <span className="text-3xl font-bold text-white">{demandData.top_locations.length}</span>
            </div>
            <p className="text-[#94A3B8] font-medium">Searched Locations</p>
            <p className="text-sm text-[#64748B] mt-1">Locations buyers are interested in</p>
          </div>
        </div>

        {/* Top Categories in Demand */}
        <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <TrendingUp className="w-6 h-6 text-[#F59E0B] mr-3" />
            Most Searched Categories
          </h2>
          <p className="text-[#94A3B8] mb-6">
            These are the mineral categories buyers are actively searching for. Consider listing in these categories for better visibility.
          </p>
          
          {demandData.top_categories.length === 0 ? (
            <p className="text-center text-[#64748B] py-8">No search data available yet</p>
          ) : (
            <div className="space-y-4">
              {demandData.top_categories.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 border border-[#1E293B] rounded-xl hover:border-amber-500 transition-colors"
                  data-testid={`category-demand-${idx}`}
                >
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-lg">
                      {item.category.replace('_', ' ').toUpperCase()}
                    </h3>
                    <p className="text-sm text-[#94A3B8]">
                      High demand - buyers actively searching
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-[#F59E0B]">{item.searches}</div>
                    <p className="text-sm text-[#94A3B8]">searches</p>
                  </div>
                  <div className="ml-6">
                    <div className="w-32 bg-[#1A2440] rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-[#F59E0B] h-full rounded-full"
                        style={{ width: `${(item.searches / demandData.top_categories[0].searches) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Locations in Demand */}
        {demandData.top_locations.length > 0 && (
          <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <MapPin className="w-6 h-6 text-[#94A3B8] mr-3" />
              Most Searched Locations
            </h2>
            <p className="text-[#94A3B8] mb-6">
              Buyers are specifically looking for minerals from these locations. Highlight your location in listings.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {demandData.top_locations.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 border border-[#1E293B] rounded-xl"
                  data-testid={`location-demand-${idx}`}
                >
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-[#64748B] mr-3" />
                    <div>
                      <h3 className="font-bold text-white">{item.location}</h3>
                      <p className="text-sm text-[#94A3B8]">{item.searches} searches</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white">{item.searches}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Card */}
        <div className="mt-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-3">Ready to List Your Minerals?</h3>
          <p className="text-amber-100 mb-6">
            Use these insights to create listings that match buyer demand and get more offers!
          </p>
          <button
            onClick={() => navigate('/seller/create-listing')}
            className="bg-[#121A2F] text-[#F59E0B] px-6 py-3 rounded-xl font-semibold hover:bg-[#0B1120] transition-colors"
          >
            Create New Listing
          </button>
        </div>
      </div>
    </div>
  );
}
